import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPosts, createPost, toggleLike, deletePost } from '../../slices/postsSlice';
import { fetchTopicDetail } from '../../slices/topicsSlice'; 
import io from 'socket.io-client';
import axios from 'axios';
import { updateFactionSuccess, clearFaction } from '../../slices/authSlice';

import PostModal from './PostModal'
import { getToxicityColor, styles } from './TopicStyles';

// 백엔드와 동일한 주차 계산 로직
const getCycleId = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

const socket = io('https://the-pivot-logical-battleground.onrender.com/');

const TopicDetail = () => {

  // complete
  const { id } = useParams(); // URL 파라미터에서 토픽 ID 추출 (id)
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedCycleRef = useRef(''); // 선택된 주차를 추적하는 ref
  const sortByRef = useRef('latest'); // 정렬 기준을 추적하는 ref
  
  // 유저 정보를 리덕스와 로컬스토리지에서 실시간으로 가져옴
  // complete(const 3개) , 보안 강화는 나중에
  const reduxUser = useSelector((state) => state.auth.user);
  const savedUser = JSON.parse(localStorage.getItem('user'));
  const user = reduxUser || savedUser;

  // complete(const 5개)
  const topic = useSelector((state) => state.topics.current);
  const [gauge, setGauge] = useState({ bluePercent: 50, redPercent: 50 });
  const posts = useSelector((state) => state.posts.list); // complete
  const [content, setContent] = useState('');
  const [selectedPost, setSelectedPost] = useState(null); // complete

  // --- [추가] 아카이브 관련 상태 ---
  const [cycles, setCycles] = useState([]); // 주차 목록 배열
  const [selectedCycle, setSelectedCycle] = useState(''); // 사용자가 선택한 주차
  const [currentCycleId, setCurrentCycleId] = useState(''); // 서버에서 판정한 현재 주차

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [sortBy, setSortBy] = useState('latest'); // 정렬 기준 상태 (기본: 최신순)

  // 참전 여부 및 관리자 여부 확인
  const isJoined = user?.joinedTopics?.includes(id);
  const isAdmin = user?.role === 'ADMIN';

  const [isChanging, setIsChanging] = useState(false);

  const isGuest = useSelector(state => state.auth.isGuest); // 🚩 게스트 상태 가져오기

  // ref에 선택된 주차와 정렬 기준 동기화
  useEffect(() => {
    selectedCycleRef.current = selectedCycle;
    sortByRef.current = sortBy;
  }, [selectedCycle, sortBy]);

  // 🚩 [추가] 상세 페이지 내에서 직접 결제하는 함수
  const handleJoinDetailed = async () => {
    if (window.confirm(`이 전장에 입성하시겠습니까?\n3,000P가 차감됩니다. (잔여: ${user?.points?.toLocaleString()}P)`)) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`https://the-pivot-logical-battleground.onrender.com/api/topics/${id}/join`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });

        dispatch(updatePoints(res.data)); // 포인트/참여목록 갱신
        alert("성공적으로 입성했습니다. 모든 논리가 해제되었습니다.");
        
        // 결제 성공 후 데이터를 즉시 다시 불러옴
        dispatch(fetchPosts({ topicId: id, cycleId: selectedCycle, sortBy }));
      } catch (err) {
        alert(err.response?.data?.message || "입성에 실패했습니다.");
      }
    }
  };
  
  // 주차 변경 시 읽기 전용 여부 판단
  useEffect(() => {
    if (selectedCycle && currentCycleId) {
      setIsReadOnly(selectedCycle !== currentCycleId);
    }
  }, [selectedCycle, currentCycleId]);

  // [수정] 데이터 패칭 useEffect
  useEffect(() => {
    if (id && selectedCycle) {
      dispatch(fetchPosts({ topicId: id, cycleId: selectedCycle, sortBy }));
    }
  }, [id, selectedCycle, sortBy, dispatch]); // sortBy를 의존성 배열에 추가

  // complete - 진영 선택 함수
  const handleJoinFaction = async (side) => {
    if (isReadOnly) return alert("종료된 전장입니다.");

    // 기존 진영과 동일한지 확인 (선택 사항)
    if (user?.currentFaction === side) {
      alert("이미 합류한 진영입니다.");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        'https://the-pivot-logical-battleground.onrender.com/api/auth/update-faction', 
        { faction: side },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      dispatch(updateFactionSuccess(response.data));

      // window.location.reload(); // 최신 상태 반영을 위한 새로고침
    } catch (err) {
      alert("진영 합류에 실패했습니다: " + (err.response?.data?.message || "서버 오류"));
      console.error("진영 변경 오류:", err);
    }
  };

  // complete - 토픽 데이터가 들어올 때마다 게이지 상태 동기화
  useEffect(() => {
    if (!topic) return;

    if (selectedCycle === currentCycleId) {
      if (topic.gauge) {
        setGauge({
          bluePercent: topic.gauge.blue,
          redPercent: topic.gauge.red
        });
      }
    } else {
      const historyData = topic.history?.find(h => h.cycleId === selectedCycle);

      if (historyData) {
        setGauge({ bluePercent: historyData.blue, redPercent: historyData.red });
      } else { setGauge({ bluePercent: 50, redPercent: 50 }); }
    }
  }, [topic, selectedCycle, currentCycleId]);

  // complete - 소켓 및 데이터 패칭
  useEffect(() => {
    if (!id) return;

    // 1. 주차 목록 및 현재 주차 정보 가져오기
    const fetchInitialData = async () => {
      try {
        const res = await axios.get(`https://the-pivot-logical-battleground.onrender.com/api/archive/list/${id}`);
        
        const realTimeCycle = getCycleId();

        let cycleList = res.data;

        if (!cycleList.includes(realTimeCycle)) {
          cycleList = [realTimeCycle, ...cycleList];
        }
        
        setCycles(cycleList);

        if (!selectedCycle) {
          setSelectedCycle(realTimeCycle);
          setCurrentCycleId(realTimeCycle); // 초기 로드 시 가장 최신을 현재 주차로 가정 (혹은 별도 API로 현재 주차 수신)
        }
      
      } catch (err) {
        console.error("주차 목록 로드 실패:", err);
      }
    };

    fetchInitialData();
    dispatch(fetchTopicDetail(id));
    socket.emit('joinTopic', id);
  
    socket.on('likeUpdated', () => { 
        // 좋아요 업데이트 시 현재 보고 있는 주차의 데이터를 다시 불러와야 함
        dispatch(fetchPosts({ topicId: id, cycleId: selectedCycleRef.current, sortBy: sortByRef.current })); 
        dispatch(fetchTopicDetail(id));
    });
    socket.on('newPost', () => dispatch(fetchPosts({ topicId: id, cycleId: selectedCycleRef.current, sortBy: sortByRef.current })));

    return () => {
        socket.off('likeUpdated');
        socket.off('newPost');
    };
  }, [id]);

  // [추가] 선택된 주차가 변경될 때마다 게시글 목록 새로고침
  useEffect(() => {
    if (id && selectedCycle) {
        // Slice가 { topicId, cycleId } 객체를 받도록 수정되었다고 가정
        dispatch(fetchPosts({ topicId: id, cycleId: selectedCycle, sortBy }));
    }
  }, [id, selectedCycle, sortBy, dispatch]);

  // complete(handlelike) - 좋아요 처리 (Slice의 toggleLike 사용)
  const handleLike = (postId, targetFaction) => {
    if (restrictGuest()) return;
    if (isReadOnly) return alert("종료된 전장입니다.");

    if (user?.currentFaction !== targetFaction) {
      alert(`${targetFaction === 'BLUE' ? '블루' : '레드'} 진영 유저만 누를 수 있습니다!`);
      return;
    }
    // const userId = user._id || user.id;
    dispatch(toggleLike({ postId }))
      .unwrap()
      .then(() => {
        dispatch(fetchTopicDetail(id)); // 좋아요 후 게이지 업데이트
      })
      .catch((err) => {
        console.error("좋아요 처리 실패:", err);
        alert("좋아요 처리에 실패했습니다: " + (err?.message || "서버 오류"));
      });
  };

  // complete(handlepost) - 게시글 생성 로직
  const handlePost = () => {
    if (restrictGuest()) return;
    if (isReadOnly) return alert("종료된 전장입니다.");

    // 1. 최신 유저 정보 확인 (리덕스 또는 로컬스토리지)
    const currentUser = reduxUser || JSON.parse(localStorage.getItem('user'));
    
    console.log("🔥 게시 시도 중인 유저 정보:", currentUser);

    if (!currentUser) return alert("로그인이 필요합니다.");
    if (!currentUser.currentFaction) return alert("진영을 먼저 선택해주세요.");
    if (content.trim().length < 5) return alert("논리적 근거를 5자 이상 입력해주세요.");

    // 2. ID 추출 (MongoDB의 _id 혹은 일반 id 대응)
    const userId = currentUser._id || currentUser.id;
    
    // 3. 전송 데이터 구성 (서버가 기대하는 필드명과 일치하는지 확인)
    const postData = { 
      topicId: id, // useParams에서 가져온 값
      content: content.trim(), 
      faction: currentUser.currentFaction,
      authorId: userId ? String(userId) : null // 객체가 아닌 문자열로 명시적 변환
    };

    // 4. 전송 전 최종 디버깅 로그
    console.log("🔥 서버로 보내는 데이터 상세:", postData);

    // 5. 400 에러를 사전에 방지하기 위한 필드 체크
    if (!postData.topicId) return alert("토픽 정보를 찾을 수 없습니다. 페이지를 새로고침해주세요.");
    if (!postData.authorId || postData.authorId === "undefined" || postData.authorId === "null") {
      return alert("작성자 인증 정보가 유효하지 않습니다. 다시 로그인해주세요.");
    }
    if (!postData.content) return alert("내용이 입력되지 않았습니다.");

    // 6. 모든 검증 통과 시 서버 전송
    dispatch(createPost(postData))
      .unwrap() // 성공/실패 여부를 확인하기 위해 추가
      .then(() => {
        setContent(''); // 성공 시에만 입력창 초기화

        // [추가] 게시글 작성이 성공했으니, 실시간 게이지 수치를 다시 가져옵니다.
        // fetchTopicDetail이 서버의 /:id/gauge 데이터를 가져오도록 설계되어 있어야 합니다.
        dispatch(fetchTopicDetail(id)); 
      })

      .catch((err) => {
        console.error("게시 실패 원인:", err);
        alert("게시글 저장에 실패했습니다: " + (err?.message || "서버 응답 오류"));
      });
  };

  // complete(handleDelete) - 게시글 삭제 처리
  const handleDelete = (postId) => {
    if (isReadOnly) return alert("종료된 전장입니다.");

    if (!window.confirm("정말 이 논리를 철회(삭제)하시겠습니까?")) return;
    dispatch(deletePost({ postId }));
  };

  // complete - 로딩 상태 처리
  if (!topic) return <div style={styles.loading}>전장의 논리를 분석 중입니다...</div>;

  // 🚩 [수정] 렌더링마다 게시글을 필터링하지 않고, posts 데이터가 바뀔 때만 연산합니다.
  const sortedBluePosts = useMemo(() => 
    [...posts].filter(p => p.faction === 'BLUE'), 
    [posts]
  );

  const sortedRedPosts = useMemo(() => 
    [...posts].filter(p => p.faction === 'RED'), 
    [posts]
  );

  /*
  // [추가] 리스트 렌더링 직전 정렬
  const sortedBluePosts = [...posts]
    .filter(p => p.faction === 'BLUE');
    // .sort((a, b) => (b.isMVP ? 1 : 0) - (a.isMVP ? 1 : 0));

  const sortedRedPosts = [...posts]
    .filter(p => p.faction === 'RED');
    // .sort((a, b) => (b.isMVP ? 1 : 0) - (a.isMVP ? 1 : 0));
  */

  // 🛡️ [핵심] 권한 체크: 관리자도 아니고 참여자도 아니라면 '잠금 화면' 렌더링
  if (!isAdmin && !isJoined && !isGuest) {
    return (
      <div style={styles.container}>
        <div style={{ 
          textAlign: 'center', 
          marginTop: '80px', 
          padding: '50px 20px', 
          background: '#161616', 
          border: '1px solid #333', 
          borderRadius: '8px',
          maxWidth: '600px',
          margin: '80px auto'
        }}>
          <h2 style={{ color: '#ff4e50', fontSize: '1.8rem', marginBottom: '20px' }}>⚠️ 참전 제한 구역</h2>
          <p style={{ color: '#aaa', lineHeight: '1.6', marginBottom: '40px' }}>
            이 전장은 보안 구역으로 설정되어 있습니다.<br />
            내부의 논리를 열람하고 참전하시려면 <br />
            <strong>입성 비용(3,000P)</strong>을 지불해야 합니다.
          </p>
          
          <button 
            onClick={handleJoinDetailed}
            style={{ ...styles.createTopicBtn, width: '100%', maxWidth: '250px', marginBottom: '20px' }}
          >
            3,000P 내고 즉시 입성
          </button>
          
          <button 
            onClick={() => navigate('/topics')}
            style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', display: 'block', width: '100%' }}
          >
            전장 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 🚩 게스트용 알림 함수
  const restrictGuest = () => {
    if (isGuest) {
      alert("전장의 기록만 열람 중입니다. 논리를 펼치려면 로그인이 필요합니다.");
      return true;
    }
    return false;
  };


  // complete - JSX 반환
  return (
    <div style={styles.container}>

      {/* 🚩 게스트 접속 시 상단 안내 배너 */}
      {isGuest && (
        <div style={{ background: '#1a1a1a', color: '#888', textAlign: 'center', padding: '10px', fontSize: '0.9rem', marginBottom: '20px' }}>
          👀 현재 '둘러보기' 모드입니다. 참전하시려면 <Link to="/login" style={{color: '#00d4ff'}}>로그인</Link>이 필요합니다.
        </div>
      )}

      {/* 읽기 전용 알림 배너 */}
      {isReadOnly && (
        <div style={{ background: '#332b00', color: '#ffd700', textAlign: 'center', padding: '10px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffd700' }}>
          📜 이 전장은 종료되었습니다. 기록된 논리만 열람 가능합니다.
        </div>
      )}

      {/* --- complete - 제목 섹션 --- */}
      {/* --- 제목 섹션 시작 --- */}
      <h1 style={{ ...styles.title, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* 메인 제목 */}
        <div style={{ fontSize: '2.2rem', marginBottom: '8px' }}>
          {topic.title.split('\n')[0].trim()} 
        </div>
      
        {/* 부제목 섹션 */}
        <div style={{ 
          fontSize: '1.3rem', 
          fontWeight: '600',
          letterSpacing: '1px',
          marginTop: '10px'
        }}>
          {/* 1. BLUE 진영 (VS 앞부분) */}
          <span style={{ color: '#00d4ff' }}>
            {topic.title.split('\n')[1]?.split('VS')[0]?.trim()}
          </span>

          {/* 2. VS (화이트) */}
          <span style={{ color: '#fff', margin: '0 15px', fontStyle: 'italic' }}>
            VS
          </span>

          {/* 3. RED 진영 (VS 뒷부분) */}
          <span style={{ color: '#ff4e50' }}>
            {topic.title.split('\n')[1]?.split('VS')[1]?.trim()}
          </span>
        </div>
      </h1>
      {/* --- 제목 섹션 끝 --- */}

      {/* 🚩 [추가] 외부 링크 버튼 섹션 */}
      {topic.externalLinks && topic.externalLinks.length > 0 && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {topic.externalLinks.map((link, index) => (
            <a 
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid #444',
                borderRadius: '20px',
                color: '#aaa',
                textDecoration: 'none',
                fontSize: '0.9rem',
                transition: 'all 0.3s'
              }}
              onMouseEnter={e => { e.target.style.borderColor = '#00d4ff'; e.target.style.color = '#fff'; }}
              onMouseLeave={e => { e.target.style.borderColor = '#444'; e.target.style.color = '#aaa'; }}
            >
              🔗 {link.linkTitle || '관련 자료 확인'}
            </a>
          ))}
        </div>
      )}

      {/* complete - 진영 선택 섹션 */}
      {(!user?.currentFaction || isChanging) ? (
        <div style={styles.factionSelector}>

          {/* 🚩 X 버튼: 이미 진영이 있는 유저가 '변경'을 시도할 때만 노출 */}
          {user?.currentFaction && (
            <button 
              onClick={() => setIsChanging(false)} 
              style={styles.factionCloseBtn}
            >
              ✕
            </button>
          )}

          <h3 style={{marginBottom: '15px'}}>어느 진영에 합류하시겠습니까? (100P 소모)</h3>
          <button onClick={() => { handleJoinFaction('BLUE'); setIsChanging(false); }} style={styles.joinBlue}>BLUE 합류</button>
          <button onClick={() => { handleJoinFaction('RED'); setIsChanging(false); }} style={styles.joinRed}>RED 합류</button>
        </div>
      ) : (
        <div style={styles.factionStatus}>
          현재 소속: <span style={user.currentFaction === 'BLUE' ? styles.blueText : styles.redText}>{user.currentFaction}</span>
          <button onClick={() => setIsChanging(true)} style={styles.resetBtn}>진영 변경</button>
        </div>
      )}
      {/* 진영 선택 섹션 끝 */}

      {/* complete - 배틀 게이지 */}
      <BattleGauge 
        bluePercent={gauge.bluePercent} 
        redPercent={gauge.redPercent} 
      />

      {/* --- [추가] 정렬 드롭다운 --- */}
      <div style={styles.sortContainer}>
        <select 
          value={sortBy} 
          onChange={(e) => setSortBy(e.target.value)}
          style={styles.sortSelect}
        >
          <option value="latest">최신순</option>
          <option value="opposing">상대 진영 좋아요순</option>
        </select>
      </div>

      {/* --- [추가] 주차 선택 드롭다운 --- */}
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <span style={{ color: '#888', marginRight: '10px' }}>전장 기록: </span>
        <select 
            value={selectedCycle} 
            onChange={(e) => setSelectedCycle(e.target.value)}
            style={{ background: '#111', color: '#fff', border: '1px solid #333', padding: '5px 10px' }}
        >
            {cycles.map(c => (
                <option key={c} value={c}>{c === currentCycleId ? `${c} (현재)` : c}</option>
            ))}
        </select>
      </div>

      {/* complete - 게시글 피드 */}
      <div style={styles.feedWrapper}>
        <div style={styles.centerLine}></div>
        <div style={styles.factionFeed}> 
          <h3 style={styles.blueHeader}>BLUE LOGIC</h3>
          {sortedBluePosts.map(post => (
            <PostCard 
              key={post._id} 
              post={post} 
              isReadOnly={isReadOnly}
              user={user} 
              onClick={() => setSelectedPost(post)} 
              onLike={handleLike} 
              onDelete={handleDelete} // 삭제 기능 연결
            />
          ))}
        </div>
        <div style={styles.factionFeed}> 
          <h3 style={styles.redHeader}>RED LOGIC</h3>
          {sortedRedPosts.map(post => (
            <PostCard 
              key={post._id} 
              post={post} 
              isReadOnly={isReadOnly}
              user={user} 
              onClick={() => setSelectedPost(post)} 
              onLike={handleLike} 
              onDelete={handleDelete} // 삭제 기능 연결
            />
          ))}
        </div>
      </div>

      {/* [추가] 입력창 섹션을 현재 주차일 때만 노출하도록 보호 */}
      {selectedCycle === currentCycleId && (
        <div style={styles.inputDock}> ... </div>
      )}

      {/* complete - 입력창 */}
      <div style={styles.inputDock}>
        <div style={styles.inputInner}>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="논리를 입력하세요.(5자 이상) / 건전한 게시판 분위기를 위해 입력한 내용은 AI에 의해 오염도가 측정됩니다.(참고용)"
            style={styles.textarea}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handlePost();
              }
            }}
          />
          <button onClick={handlePost} style={{...styles.postBtn, background: user?.currentFaction === 'BLUE' ? '#00d4ff' : '#ff4e50'}}>게시</button>
        </div>
      </div>
      
      {/* 공통 모달 호출 */}
      <PostModal post={selectedPost} user={user} onClose={() => setSelectedPost(null)} topicId={id} />
    </div>
  );
};

// complete - 게이지바
const BattleGauge = ({ bluePercent, redPercent }) => {
  return (
    <div style={styles.gaugeContainer}>

      <div style={styles.gaugeLabel}>
        <span style={styles.blueText}>BLUE {bluePercent}%</span>
        <span style={styles.redText}>RED {redPercent}%</span>
      </div>
      
      <div style={styles.gaugeBarTrack}>
        {/* BLUE 영역 */}
        <div style={{
          ...styles.gaugeBarBlue,
          width: `${bluePercent}%`,
          transition: 'width 0.5s ease-in-out' // 실시간 변화 애니메이션
        }} />
        {/* RED 영역은 나머지를 자동으로 채움 */}
        <div style={{ 
          ...styles.gaugeBarRed, 
          width: `${redPercent}%`,
          transition: 'width 0.5s ease-in-out'
        }} />
      </div>

    </div>
  );
};

// complete(PostCard)
const PostCard = ({ post, isReadOnly, user, onClick, onLike, onDelete }) => {
  //console.log("이 카드의 데이터:", post); 
  //console.log("이 카드의 진영 정보:", post.faction);

  const isBlue = post.faction === 'BLUE';
  const isMVP = post.isMVP;
  const userFaction = user?.currentFaction;
  const isMyPost = post.authorId?._id === (user?._id || user?.id);

  const toxicityScore = post.perspectiveScore?.total || 0;

  // [추가] MVP 전용 스타일
  const mvpCardStyle = isMVP ? {
    border: '2px solid #ffd700',
    boxShadow: '0 0 15px rgba(255, 215, 0, 0.3)',
    transform: 'translateY(-3px)',
  } : {};

  return (
    <div 
      style={{ 
        ...styles.postCard, 
        ...mvpCardStyle,
        borderLeft: isBlue ? '4px solid #00d4ff' : 'none', 
        borderRight: isBlue ? 'none' : '4px solid #ff4e50',
        position: 'relative', // 뱃지용
      }}
    >
      {/* [추가] MVP 뱃지 */}
      {isMVP && (
        <div style={{ position: 'absolute', top: '-15px', [isBlue ? 'left' : 'right']: '10px', fontSize: '1.5rem' }}>
          🏆
        </div>
      )}

      {/* 1. 내용 영역 (클릭 시 모달 오픈) */}
      <div onClick={onClick}>
        <div style={{
            fontSize: '0.8rem',
            marginBottom: '6px',
            color: isMVP ? '#ffd700' : (isBlue ? '#00d4ff' : '#ff4e50'),
            fontWeight: '600' 
        }}>
          {isMVP && '[BEST LOGIC] '} {post.authorId?.nickname}

          {/* 🚩 [추가] 오염도 표시 배지 */}
          <span style={{
            ...styles.toxicityBadge,
            color: getToxicityColor(toxicityScore)
          }}>
            오염도 {(toxicityScore * 100).toFixed(1)}%
          </span>

        </div>
        <p style={styles.postContent}>{post.content}</p>
      </div>

      {/* 2. 하단 액션 영역 (좋아요 + 삭제) */}
      <div style={styles.likeSection}>
        {/* 블루 좋아요 */}
        <button 
          onClick={(e) => { if (isReadOnly) return; e.stopPropagation(); onLike(post._id, 'BLUE'); }} 
          style={{ ...styles.likeBtn, color: '#00d4ff', opacity: userFaction === 'BLUE' ? 1 : 0.3 }}
        >
          💙 {post.likes_blue || 0}
        </button>

        {/* 레드 좋아요 */}
        <button 
          onClick={(e) => { if (isReadOnly) return; e.stopPropagation(); onLike(post._id, 'RED'); }} 
          style={{ ...styles.likeBtn, color: '#ff4e50', opacity: userFaction === 'RED' ? 1 : 0.3 }}
        >
          ❤️ {post.likes_red || 0}
        </button>

        {/* 댓글 수 표시 */}
        <div 
          onClick={onClick}
          style={{ ...styles.commentCount, color: '#888' }}
        >
          💬 {post.commentCount || 0}
        </div>

        {/* [추가] 삭제 버튼: 내가 쓴 글일 때만 표시 */}
        {isMyPost && (
          <button 
            onClick={(e) => { if (isReadOnly) return; e.stopPropagation(); onDelete(post._id); }} 
            style={styles.deleteBtn}
          >
            삭제
          </button>
        )}
      </div>
    </div>
  );
};

export default TopicDetail;